import { useMutation, useQueryClient } from '@tanstack/react-query';
import { type FormEvent, useState } from 'react';

import { MutationError } from '../../components/MutationError';
import { useProject } from '../../projects/context';
import { useProjectApi } from '../../projects/useProjectApi';
import { refreshKeys } from './contactEdit';
import { NotesView } from './NotesView';

// Notizen zum Lead (Thomas 12.10.2026: „In der Akquise kann ich keine Notizen speichern“): bisherige Notizen lesen und eine
// neue Notiz speichern – jederzeit, auch ohne Anruf. Sie wird mit Datum, Uhrzeit und Verfasser angehängt (bestehende Notizen,
// auch aus Dialfire, bleiben) und steht in der Historie. Der Entwurf gehört zu genau einem Lead (draft.id). Am Termin
// (appointmentId) hält der Makler seine Erlebnisse fest – auch nach dem Termin; die Notiz steht dann auch im Verlauf des Termins.
export function LeadNotes({
  leadId,
  notes,
  appointmentId,
  placeholder = 'z. B. nur vormittags erreichbar, Tochter entscheidet mit',
  hint,
}: {
  leadId: string;
  notes: string | null | undefined;
  appointmentId?: string;
  placeholder?: string;
  hint?: string;
}) {
  const { project, can } = useProject();
  const papi = useProjectApi();
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState({ id: leadId, text: '' });
  const [savedFor, setSavedFor] = useState<string | null>(null);
  const text = draft.id === leadId ? draft.text : '';
  const save = useMutation({
    mutationFn: (v: { id: string; text: string }) => papi.appendLeadNote(v.id, v.text, appointmentId),
    onSuccess: (_, v) => {
      setDraft({ id: v.id, text: '' });
      setSavedFor(v.id);
      void queryClient.invalidateQueries({ predicate: (q) => refreshKeys(q.queryKey, project.id) });
    },
  });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (text.trim()) save.mutate({ id: leadId, text: text.trim() });
  };
  return (
    <section className="lead-notes-panel" aria-label="Notizen zum Lead">
      <h3>Notizen</h3>
      {hint && <p className="muted small">{hint}</p>}
      <NotesView notes={notes} />
      {can('lead.edit') && (
        <form onSubmit={submit}>
          <label className="field wide">
            <span>Neue Notiz</span>
            <textarea
              rows={2}
              maxLength={4000}
              value={text}
              placeholder={placeholder}
              onChange={(e) => {
                setSavedFor(null);
                setDraft({ id: leadId, text: e.target.value });
              }}
            />
          </label>
          <div className="form-actions">
            <button type="submit" className="btn btn-small btn-primary" disabled={!text.trim() || save.isPending}>
              Notiz speichern
            </button>
            {savedFor === leadId && (
              <span className="muted small" role="status">
                Notiz gespeichert.
              </span>
            )}
          </div>
          {save.isError && <MutationError error={save.error} />}
        </form>
      )}
    </section>
  );
}
